import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';

import { getProgressColor } from '../../helpers/progress-color.helper';
import { calculateProgressPercentage } from '../../helpers/progress-percentage.helper';
import { useTimeTrackingStore, WeekProgressBasis } from '../../store/time-tracking.store';
import { WeeklySummary } from '../../types/time-tracking';
import { WeekSlot } from './week-slot';

interface WeekTotalsRowProps {
  slots: WeekSlot[];
  summary: WeeklySummary;
}

/**
 * Bottom row of the week grid showing daily totals + weekly aggregate.
 * The left "Day total" cell exposes a tabs control letting the user switch
 * the denominator of every progress bar in the row between two bases:
 * **availability** (effective capacity) or **booked** (planned hours). The
 * numerator is always plain tracked hours — time-off is never folded in, in
 * either basis.
 */
export function WeekTotalsRow({ slots, summary }: WeekTotalsRowProps) {
  const basis = useTimeTrackingStore(state => state.weekProgressBasis);
  return (
    <>
      <TotalsBasisCell />
      {slots.map((slot, i) => (
        <DailyTotalCell key={i} slot={slot} basis={basis} />
      ))}
      <WeeklyTotalCell summary={summary} basis={basis} />
    </>
  );
}

function TotalsBasisCell() {
  const { t } = useTranslation('authenticated.timeTracking');
  const basis = useTimeTrackingStore(state => state.weekProgressBasis);
  const setBasis = useTimeTrackingStore(state => state.setWeekProgressBasis);

  return (
    <div className="flex flex-col items-start gap-2 bg-muted/40 px-3 py-2.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('weekView.totalsRow.label')}
      </span>
      <Tabs value={basis} onValueChange={value => setBasis(value as WeekProgressBasis)}>
        <TabsList className="inline-flex h-8 w-auto border border-border bg-muted p-0.5">
          <TabsTrigger value={WeekProgressBasis.Availability} className="px-2 py-0.5 text-[11px]">
            {t('weekView.totalsRow.basisAvailability')}
          </TabsTrigger>
          <TabsTrigger value={WeekProgressBasis.Booked} className="px-2 py-0.5 text-[11px]">
            {t('weekView.totalsRow.basisBooked')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
}

function DailyTotalCell({ slot, basis }: { slot: WeekSlot; basis: WeekProgressBasis }) {
  const { t } = useTranslation('authenticated.timeTracking');
  const day = slot.breakdown;
  const tracked = day?.trackedHours ?? 0;
  const booked = day?.bookedHours ?? 0;
  const rawCapacity = day?.dailyTotalCapacityHours ?? 0;
  // Effective capacity = raw capacity minus approved time-off. This is what
  // "availability" represents — a full-time-off day reads 0h available even
  // though its raw capacity is still 8h (OPS-1535).
  const effectiveCapacity = day?.dailyCapacityHours ?? 0;

  // Both bases compare plain tracked hours (time-off is never folded in) —
  // only the denominator switches: effective capacity for availability,
  // booked hours for booked.
  const isAvailability = basis === WeekProgressBasis.Availability;
  const denom = isAvailability ? effectiveCapacity : booked;

  if (rawCapacity <= 0 && booked <= 0) {
    return (
      <div className="flex flex-col items-center gap-1 border-l bg-muted/40 px-2 py-2.5">
        <span className="text-sm font-semibold tabular-nums text-muted-foreground">
          {t('weekView.totalsRow.empty')}
        </span>
        <span className="text-[10.5px] text-muted-foreground">
          {t('weekView.totalsRow.weekend')}
        </span>
      </div>
    );
  }

  return (
    <div className="border-l bg-muted/40 px-3 py-2.5">
      <TotalCellContent
        tracked={tracked}
        denom={denom}
        basis={basis}
        capacity={effectiveCapacity}
        booked={booked}
      />
    </div>
  );
}

function WeeklyTotalCell({ summary, basis }: { summary: WeeklySummary; basis: WeekProgressBasis }) {
  // Effective capacity (raw minus approved time-off) so the weekly availability
  // total matches the per-day cells (OPS-1535).
  const capacity = summary.effectiveCapacityHours;
  const booked = summary.totalBookedHours;
  const isAvailability = basis === WeekProgressBasis.Availability;
  const denom = isAvailability ? capacity : booked;

  return (
    <div className="border-l bg-muted/40 px-3 py-2.5 text-right">
      <TotalCellContent
        tracked={summary.totalTrackedHours}
        denom={denom}
        basis={basis}
        capacity={capacity}
        booked={booked}
        showEmptyBookedFallback={false}
      />
    </div>
  );
}

interface TotalCellContentProps {
  tracked: number;
  denom: number;
  basis: WeekProgressBasis;
  capacity: number;
  booked: number;
  /** Used by the daily cell to fall back to "—" when no booked + no capacity. */
  showEmptyBookedFallback?: boolean;
}

/** Shared progress-bar layout used by both the daily and weekly totals cells. */
function TotalCellContent({
  tracked,
  denom,
  basis,
  capacity,
  booked,
  showEmptyBookedFallback = true,
}: TotalCellContentProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  // In "by availability" mode, 0 available (a full-time-off day) with 0 tracked
  // means nothing was owed and nothing is missing — that's a complete day, not a
  // 0% one, so it must read green rather than red. "By booked" mode has no such
  // special case: 0 booked with 0 tracked falls through to the shared helper.
  const progressPct =
    basis === WeekProgressBasis.Availability && denom <= 0
      ? 100
      : calculateProgressPercentage(tracked, denom);
  const pct = progressPct / 100;
  const color = getProgressColor(progressPct);
  const widthPct = Math.min(100, progressPct);

  const valueClass =
    pct > 1.1 ? 'text-destructive' : pct < 0.9 && tracked > 0 ? 'text-warning' : '';

  return (
    <div className="flex flex-col items-stretch gap-1">
      <span className={cn('text-sm font-semibold tabular-nums', valueClass)}>
        {formatHoursToTimeString(tracked)}
      </span>
      <div className="h-1 overflow-hidden rounded-full bg-track-empty">
        <div
          className="h-full rounded-full"
          style={{ width: `${widthPct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-right text-[10.5px] tabular-nums text-muted-foreground">
        {basis === WeekProgressBasis.Availability
          ? t('weekView.totalsRow.availableShort', { hours: formatHoursToTimeString(capacity) })
          : booked > 0
            ? t('weekView.totalsRow.bookedShort', { hours: formatHoursToTimeString(booked) })
            : showEmptyBookedFallback
              ? t('weekView.totalsRow.empty')
              : t('weekView.totalsRow.bookedShort', { hours: formatHoursToTimeString(0) })}
      </span>
    </div>
  );
}
